import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Card, Select, DatePicker, Button, Radio, Checkbox, Space, message, Spin, Empty, Segmented, Tag, Collapse, Switch, ColorPicker, Input, Tooltip, Progress } from 'antd';
import { CameraOutlined, DownloadOutlined, ReloadOutlined, UnorderedListOutlined, PictureOutlined, StarOutlined, StarFilled, AppstoreOutlined, SettingOutlined, FontSizeOutlined, BgColorsOutlined, LeftOutlined, RightOutlined, FileImageOutlined, VideoCameraOutlined } from '@ant-design/icons';
import moment from 'moment';
import { toPng } from 'html-to-image';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { fetchEventsForCalendar } from '../../server/publications/publicationServer';
import { getActiveCountries } from '../../server/configuration/FiltersServer';
import logoAppIcon from '../../assets/images/logo/logomyswayapp.png';

const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Hero display font (heavy condensed; loaded independently of the body font) ──
const HERO_FONT = "'Anton', sans-serif";

// ── Per-city default accent (applied automatically when a city is selected; still overridable via the color picker) ──
const CITY_ACCENTS = {
  montreal: '#a855f7',     // purple
  toronto: '#ef4444',      // red
  'new york': '#3b82f6',   // blue
  nyc: '#3b82f6',
  'new york city': '#3b82f6',
  'los angeles': '#ec4899', // pink
  la: '#ec4899',
  london: '#f59e0b',       // amber
  paris: '#06b6d4',        // cyan
  berlin: '#10b981',       // emerald
  tokyo: '#f43f5e',        // rose
  miami: '#22d3ee',        // cyan-bright
  chicago: '#facc15',      // yellow
  vancouver: '#14b8a6',    // teal
};
const accentForCity = (name) => (name ? CITY_ACCENTS[name.toLowerCase().trim()] || null : null);

// ── Theme definitions ──
const THEMES = {
  dark: {
    label: 'Dark', swatch: '#111',
    bg: 'linear-gradient(170deg, #000000 0%, #0a0a0a 25%, #111 55%, #0a0a0a 100%)',
    text: '#fff', textMuted: 'rgba(255,255,255,0.55)', textSoft: 'rgba(255,255,255,0.4)', textFaint: 'rgba(255,255,255,0.3)',
    accent: '#a855f7',
    orbA: 'rgba(255,255,255,0.04)', orbB: 'rgba(255,255,255,0.03)',
    cardBg: 'rgba(255,255,255,0.03)', cardBorder: 'rgba(255,255,255,0.06)',
    featuredBg: 'rgba(168,85,247,0.12)', featuredBorder: 'rgba(168,85,247,0.4)',
    tagBg: 'rgba(255,255,255,0.06)', tagBorder: 'rgba(255,255,255,0.1)', tagText: 'rgba(255,255,255,0.7)',
    divider: 'rgba(255,255,255,0.12)',
    badgeBg: 'rgba(255,255,255,0.05)', badgeBorder: 'rgba(255,255,255,0.2)', badgeText: 'rgba(255,255,255,0.6)',
    iconFill: 'white', logoFilter: 'brightness(0) invert(1)',
    flyerShadow: 'rgba(255,255,255,0.06)', flyerBorder: 'rgba(255,255,255,0.08)',
    spotlight: 'linear-gradient(212deg, rgba(180,130,255,0.55) 0%, rgba(168,85,247,0.22) 22%, rgba(168,85,247,0.05) 42%, rgba(168,85,247,0) 60%)',
    heroFrom: '#f3e8ff', heroTo: '#a855f7',
    timelineLine: 'rgba(168,85,247,0.35)', timelineDot: '#a855f7', timelineGlow: 'rgba(168,85,247,0.55)', timelineRing: 'rgba(168,85,247,0.15)',
    pillBg: 'rgba(255,255,255,0.05)', pillBorder: 'rgba(255,255,255,0.09)',
    footerTileBg: '#ffffff',
  },
  white: {
    label: 'White', swatch: '#fff',
    bg: '#ffffff',
    text: '#111', textMuted: 'rgba(0,0,0,0.55)', textSoft: 'rgba(0,0,0,0.4)', textFaint: 'rgba(0,0,0,0.25)',
    accent: '#a855f7',
    orbA: 'rgba(168,85,247,0.06)', orbB: 'rgba(168,85,247,0.04)',
    cardBg: 'rgba(0,0,0,0.02)', cardBorder: 'rgba(0,0,0,0.06)',
    featuredBg: 'rgba(168,85,247,0.06)', featuredBorder: 'rgba(168,85,247,0.35)',
    tagBg: 'rgba(0,0,0,0.04)', tagBorder: 'rgba(0,0,0,0.08)', tagText: 'rgba(0,0,0,0.6)',
    divider: 'rgba(0,0,0,0.1)',
    badgeBg: 'rgba(0,0,0,0.03)', badgeBorder: 'rgba(0,0,0,0.15)', badgeText: 'rgba(0,0,0,0.45)',
    iconFill: '#111', logoFilter: 'none',
    flyerShadow: 'rgba(0,0,0,0.06)', flyerBorder: 'rgba(0,0,0,0.08)',
    spotlight: 'linear-gradient(212deg, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0.07) 24%, rgba(124,58,237,0.02) 44%, rgba(124,58,237,0) 60%)',
    heroFrom: '#a855f7', heroTo: '#6d28d9',
    timelineLine: 'rgba(124,58,237,0.3)', timelineDot: '#7c3aed', timelineGlow: 'rgba(124,58,237,0.4)', timelineRing: 'rgba(124,58,237,0.12)',
    pillBg: 'rgba(0,0,0,0.03)', pillBorder: 'rgba(0,0,0,0.08)',
    footerTileBg: '#f4f2fb',
  },
  purple: {
    label: 'Purple', swatch: '#7c3aed',
    bg: 'linear-gradient(170deg, #000000 0%, #0a0a0a 20%, #2d1040 50%, #1a0a2e 75%, #120822 100%)',
    text: '#fff', textMuted: 'rgba(255,255,255,0.6)', textSoft: 'rgba(255,255,255,0.45)', textFaint: 'rgba(255,255,255,0.3)',
    accent: '#c084fc',
    orbA: 'rgba(180,50,160,0.2)', orbB: 'rgba(160,40,200,0.15)',
    cardBg: 'rgba(255,255,255,0.04)', cardBorder: 'rgba(255,255,255,0.07)',
    featuredBg: 'rgba(192,132,252,0.15)', featuredBorder: 'rgba(192,132,252,0.45)',
    tagBg: 'rgba(255,255,255,0.06)', tagBorder: 'rgba(255,255,255,0.1)', tagText: 'rgba(255,255,255,0.7)',
    divider: 'rgba(255,255,255,0.15)',
    badgeBg: 'rgba(255,255,255,0.05)', badgeBorder: 'rgba(255,255,255,0.2)', badgeText: 'rgba(255,255,255,0.6)',
    iconFill: 'white', logoFilter: 'brightness(0) invert(1)',
    flyerShadow: 'rgba(255,255,255,0.06)', flyerBorder: 'rgba(255,255,255,0.08)',
    spotlight: 'linear-gradient(212deg, rgba(232,180,255,0.6) 0%, rgba(192,132,252,0.26) 22%, rgba(192,132,252,0.07) 42%, rgba(192,132,252,0) 60%)',
    heroFrom: '#f5d0fe', heroTo: '#c084fc',
    timelineLine: 'rgba(192,132,252,0.4)', timelineDot: '#c084fc', timelineGlow: 'rgba(192,132,252,0.6)', timelineRing: 'rgba(192,132,252,0.18)',
    pillBg: 'rgba(255,255,255,0.06)', pillBorder: 'rgba(255,255,255,0.1)',
    footerTileBg: '#ffffff',
  },
};

// ── Format configs ──
const FORMAT_CONFIGS = {
  story: { label: '9:16 Story', width: 1080, height: 1920, previewScale: 0.35 },
  post: { label: '1:1 Post', width: 1080, height: 1080, previewScale: 0.55 },
};

// ── Font options ──
const FONT_OPTIONS = [
  { key: 'inter', label: 'Inter', family: "'Inter', sans-serif", weights: '400;500;600;700;800;900', category: 'Clean' },
  { key: 'playfair', label: 'Playfair Display', family: "'Playfair Display', serif", weights: '400;500;600;700;800;900', category: 'Elegant' },
  { key: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", weights: '400;500;600;700;800;900', category: 'Modern' },
  { key: 'space-grotesk', label: 'Space Grotesk', family: "'Space Grotesk', sans-serif", weights: '400;500;600;700', category: 'Tech' },
  { key: 'bebas-neue', label: 'Bebas Neue', family: "'Bebas Neue', sans-serif", weights: '400', category: 'Bold' },
  { key: 'outfit', label: 'Outfit', family: "'Outfit', sans-serif", weights: '400;500;600;700;800;900', category: 'Rounded' },
];

// ── Export quality ──
const EXPORT_QUALITY = [
  { key: 1, label: '1x' },
  { key: 2, label: '2x HD' },
  { key: 3, label: '3x Ultra' },
];

// ── Batch layouts ──
const BATCH_LAYOUTS = {
  cards: { label: 'Cards' },
  minimal: { label: 'Minimal' },
};

// ── Font loader ──
const loadedFonts = new Set(['inter']);
const loadGoogleFont = (fontOption) => {
  if (loadedFonts.has(fontOption.key)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontOption.label.replace(/ /g, '+')}:wght@${fontOption.weights}&display=swap`;
  document.head.appendChild(link);
  loadedFonts.add(fontOption.key);
};

// Hero font (Anton) — preloaded once so it's ready before html-to-image export
const loadHeroFont = () => {
  if (loadedFonts.has('anton')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Anton&display=swap';
  document.head.appendChild(link);
  loadedFonts.add('anton');
};

// ── Theme resolver (merges accent override) ──
const resolveTheme = (themeKey, accentOverride) => {
  const base = THEMES[themeKey];
  if (!accentOverride) return base;
  return {
    ...base,
    accent: accentOverride,
    featuredBg: `${accentOverride}1F`,
    featuredBorder: `${accentOverride}66`,
    heroTo: accentOverride,
    timelineDot: accentOverride,
    timelineGlow: `${accentOverride}99`,
    timelineRing: `${accentOverride}26`,
  };
};

// ── Helpers ──
const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
    if (c && typeof c === 'object') {
      const s = c.name || c.displayName || c.username || c.label || c.value;
      if (typeof s === 'string' && s.trim()) return s.trim();
    }
  }
  return '';
};

const extractEvent = (raw) => {
  const d = raw.publicationData || raw;
  // The club/profile/creator name lives under many possible fields depending on the publication source.
  // Try them in priority order (most specific → fallback).
  const venue = pickString(
    d.organisationName,
    d.organizerName,
    d.promoterName,
    d.venueName,
    d.locationName,
    d.clubName,
    d.hostName,
    d.organisation,
    d.organization,
    d.organizer,
    d.promoter,
    d.host,
    d.venue,
    d.location,
    d.club,
    d.profile,
    d.profileName,
    d.creator,
    d.creatorName,
    raw.profile,
    raw.creator,
    raw.organisation,
    raw.host,
  );
  return {
    _id: raw._id,
    title: d.title || 'Untitled',
    flyer: d.flyer || '',
    startTime: d.startTime,
    endTime: d.endTime,
    venue,
    city: d.city?.value || d.city || d.localisation?.city?.value || d.localisation?.city || '',
    artists: d.tags || d.lineup || [],
  };
};

// ── Hero header (left-aligned poster title) ──
const HERO_SIZES = {
  batch: {
    story: { pad: '88px 70px 0', spark: 34, kicker: 86, city: 104, line: 92, sub: 30 },
    post: { pad: '44px 50px 0', spark: 24, kicker: 56, city: 70, line: 64, sub: 22 },
  },
  single: {
    story: { pad: '70px 70px 0', spark: 28, kicker: 46, city: 66, line: 72, sub: 26 },
    post: { pad: '34px 50px 0', spark: 20, kicker: 34, city: 46, line: 50, sub: 18 },
  },
};

const StoryHero = ({ t, kicker, city, subtitle, isPost, variant = 'batch' }) => {
  const s = HERO_SIZES[variant][isPost ? 'post' : 'story'];
  return (
    <div style={{ padding: s.pad }}>
      <div style={{ fontSize: s.spark, color: t.accent, lineHeight: 1, marginBottom: isPost ? 8 : 14 }}>✦</div>
      {kicker && (
        <div style={{ fontFamily: HERO_FONT, fontWeight: 800, color: t.text, fontSize: s.kicker, lineHeight: 0.92, letterSpacing: 1, textTransform: 'uppercase' }}>
          {kicker}
        </div>
      )}
      {city && (
        <div style={{
          fontFamily: HERO_FONT, fontWeight: 800, fontSize: s.city, lineHeight: 0.92, letterSpacing: 1, textTransform: 'uppercase',
          color: t.accent, // fallback if background-clip:text doesn't render
          backgroundImage: `linear-gradient(180deg, ${t.heroFrom} 0%, ${t.heroTo} 100%)`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {city}
        </div>
      )}
      <div style={{ width: s.line, height: isPost ? 4 : 5, borderRadius: 3, background: t.accent, margin: isPost ? '14px 0 0' : '24px 0 0' }} />
      {subtitle && (
        <div style={{ fontSize: s.sub, fontWeight: 600, color: t.textMuted, letterSpacing: 4, textTransform: 'uppercase', marginTop: isPost ? 12 : 18 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

// ── App store badge SVGs ──
const AppleIcon = ({ fill }) => (
  <svg width="22" height="26" viewBox="0 0 22 26" fill={fill} xmlns="http://www.w3.org/2000/svg">
    <path d="M18.07 13.62c-.03-3.22 2.63-4.77 2.75-4.84-1.5-2.19-3.83-2.49-4.66-2.52-1.98-.2-3.87 1.17-4.88 1.17-1.01 0-2.56-1.14-4.21-1.11-2.17.03-4.17 1.26-5.29 3.2-2.25 3.91-.58 9.7 1.62 12.87 1.07 1.55 2.35 3.3 4.03 3.23 1.62-.06 2.23-1.05 4.18-1.05 1.96 0 2.5 1.05 4.21 1.02 1.74-.03 2.84-1.58 3.9-3.14 1.23-1.8 1.73-3.55 1.76-3.64-.04-.02-3.38-1.3-3.41-5.15zM14.87 3.94C15.75 2.87 16.35 1.4 16.19 0c-1.29.05-2.86.86-3.78 1.94-.83.96-1.55 2.49-1.36 3.96 1.44.11 2.91-.73 3.82-1.96z"/>
  </svg>
);

const PlayIcon = ({ fill }) => (
  <svg width="22" height="24" viewBox="0 0 22 24" fill={fill} xmlns="http://www.w3.org/2000/svg">
    <path d="M1.57.46C1.2.85 1 1.47 1 2.27v19.46c0 .8.2 1.42.57 1.81l.1.09L12.2 13.1v-.2L1.67.37l-.1.09z"/>
    <path d="M15.72 16.62l-3.52-3.52v-.2l3.52-3.52.08.05 4.17 2.37c1.19.68 1.19 1.79 0 2.46l-4.17 2.37-.08.05v-.06z"/>
    <path d="M15.8 16.57L12.2 12.97 1.57 23.54c.39.42 1.04.47 1.78.05L15.8 16.57z"/>
    <path d="M15.8 7.38L3.35.36C2.61-.06 1.96-.01 1.57.41L12.2 11.03l3.6-3.65z"/>
  </svg>
);

// Solid black store badge (reads like the real App Store / Google Play badge on any theme)
const StoreBadge = ({ icon, top, bottom, compact }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 7 : 11, padding: compact ? '7px 14px' : '11px 22px', borderRadius: compact ? 10 : 12, background: '#000', border: '1px solid rgba(255,255,255,0.22)' }}>
    {icon}
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: compact ? 9 : 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>{top}</div>
      <div style={{ fontSize: compact ? 14 : 18, fontWeight: 600, color: '#fff', lineHeight: 1.3, letterSpacing: 0.2 }}>{bottom}</div>
    </div>
  </div>
);

const StoryFooter = ({ t, showFooter = true, showLogo = true, compact }) => {
  if (!showFooter) return null;
  const tile = compact ? 70 : 96;
  return (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: compact ? '0 50px 34px' : '0 70px 54px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: compact ? 18 : 28 }}>
        <div style={{ flex: 1, height: 1, background: t.divider }} />
        <span style={{ color: t.accent, fontSize: compact ? 14 : 18, lineHeight: 1 }}>✦</span>
        <div style={{ flex: 1, height: 1, background: t.divider }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 16 : 22, marginBottom: compact ? 16 : 24 }}>
        {showLogo && (
          <img
            src={logoAppIcon}
            alt=""
            crossOrigin="anonymous"
            style={{ width: tile, height: tile, borderRadius: compact ? 16 : 22, objectFit: 'cover', flexShrink: 0, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}
          />
        )}
        <div>
          <div style={{ fontSize: compact ? 24 : 32, fontWeight: 800, color: t.text, lineHeight: 1.1, letterSpacing: 0.3 }}>Your City. Your Sound.</div>
          <div style={{ fontSize: compact ? 14 : 18, fontWeight: 400, color: t.textMuted, marginTop: 5, lineHeight: 1.35 }}>Discover events, artists & nightlife near you.</div>
          <div style={{ fontSize: compact ? 14 : 18, fontWeight: 400, color: t.textMuted, marginTop: 2, lineHeight: 1.35 }}>
            Download <span style={{ color: t.accent, fontWeight: 700 }}>Mysway</span> on iOS &amp; Android.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: compact ? 10 : 14 }}>
        <StoreBadge icon={<AppleIcon fill="#fff" />} top="Download on the" bottom="App Store" compact={compact} />
        <StoreBadge icon={<PlayIcon fill="#fff" />} top="Get it on" bottom="Google Play" compact={compact} />
      </div>
    </div>
  );
};

// ── Single Event Story Template ──
const SingleStoryCanvas = React.forwardRef(({ event, theme, format = 'story', fontFamily, accentColor, cityName, heroPrefix, showLogo, showFooter, customTitle, customSubtitle }, ref) => {
  if (!event) return null;
  const t = resolveTheme(theme, accentColor);
  const f = FORMAT_CONFIGS[format];
  const isPost = format === 'post';
  const dateLine = customSubtitle || (event.startTime ? moment(event.startTime).format('dddd DD MMMM') : '');
  const timeLine = event.startTime
    ? `${moment(event.startTime).format('HH:mm')}${event.endTime ? ` — ${moment(event.endTime).format('HH:mm')}` : ''}`
    : '';

  return (
    <div
      ref={ref}
      style={{
        width: f.width, height: f.height,
        background: t.bg,
        position: 'relative', overflow: 'hidden',
        fontFamily: fontFamily || "'Inter', sans-serif", color: t.text,
      }}
    >
      {/* Spotlight beam from the top-right corner */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: '78%', height: '52%', background: t.spotlight, filter: 'blur(6px)', pointerEvents: 'none' }} />

      <StoryHero t={t} kicker={customTitle || heroPrefix} city={cityName} subtitle={dateLine} isPost={isPost} variant="single" />

      {isPost ? (
        /* ── 1:1 Post layout: side-by-side ── */
        <div style={{ display: 'flex', gap: 32, padding: '14px 50px 0', height: f.height - 360 }}>
          <div style={{ flexShrink: 0 }}>
            {event.flyer ? (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', inset: -10, borderRadius: 22, background: t.flyerShadow, filter: 'blur(15px)' }} />
                <img src={event.flyer} alt="" crossOrigin="anonymous" style={{ width: 420, height: 420, objectFit: 'cover', borderRadius: 18, position: 'relative', zIndex: 1, border: `1px solid ${t.flyerBorder}` }} />
              </div>
            ) : (
              <div style={{ width: 420, height: 420, borderRadius: 18, background: t.cardBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 50, color: t.textFaint }}>No Flyer</div>
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
            <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1.15, marginBottom: 12 }}>{event.title}</div>
            {timeLine && <div style={{ fontSize: 26, fontWeight: 800, color: t.accent, marginBottom: 10, letterSpacing: 0.5 }}>{timeLine}</div>}
            {event.venue && <div style={{ fontSize: 20, fontWeight: 500, color: t.textSoft, marginBottom: 14 }}>{event.venue}</div>}
            {event.artists.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {event.artists.slice(0, 4).map((a, i) => (
                  <span key={i} style={{ padding: '5px 14px', borderRadius: 50, background: t.tagBg, border: `1px solid ${t.tagBorder}`, fontSize: 17, fontWeight: 500, color: t.tagText }}>{a}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ── 9:16 Story layout: vertical ── */
        <>
          <div style={{ padding: '30px 70px 0', display: 'flex', justifyContent: 'center' }}>
            {event.flyer ? (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', inset: -15, borderRadius: 28, background: t.flyerShadow, filter: 'blur(20px)' }} />
                <img src={event.flyer} alt="" crossOrigin="anonymous" style={{ width: 700, height: 700, objectFit: 'cover', borderRadius: 24, position: 'relative', zIndex: 1, border: `1px solid ${t.flyerBorder}` }} />
              </div>
            ) : (
              <div style={{ width: 700, height: 700, borderRadius: 24, background: t.cardBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, color: t.textFaint }}>No Flyer</div>
            )}
          </div>
          <div style={{ padding: '36px 70px 0' }}>
            <div style={{ fontSize: 50, fontWeight: 800, lineHeight: 1.15, marginBottom: 14 }}>{event.title}</div>
            {timeLine && <div style={{ fontSize: 36, fontWeight: 800, color: t.accent, marginBottom: 14, letterSpacing: 0.5 }}>{timeLine}</div>}
            {event.venue && <div style={{ fontSize: 28, fontWeight: 500, color: t.textSoft, marginBottom: 18 }}>{event.venue}</div>}
            {event.artists.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {event.artists.slice(0, 6).map((a, i) => (
                  <span key={i} style={{ padding: '7px 18px', borderRadius: 50, background: t.tagBg, border: `1px solid ${t.tagBorder}`, fontSize: 21, fontWeight: 500, color: t.tagText }}>{a}</span>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <StoryFooter t={t} showFooter={showFooter} showLogo={showLogo} compact={isPost} />
    </div>
  );
});

// ── Batch Story Template ──
const BatchStoryCanvas = React.forwardRef(({ events, dayLabel, cityName, heroPrefix, theme, featuredIds, format = 'story', fontFamily, accentColor, showLogo, showFooter, customTitle, customSubtitle, batchLayout = 'cards', pageLabel }, ref) => {
  if (!events || events.length === 0) return null;
  const t = resolveTheme(theme, accentColor);
  const f = FORMAT_CONFIGS[format];
  const isPost = format === 'post';
  const isMinimal = batchLayout === 'minimal';

  const shown = events;
  const n = shown.length;
  const rowGap = isPost ? 12 : 18;
  const headerSpace = isPost ? 300 : 470;
  const footerSpace = isPost ? 200 : 330;
  const availableHeight = f.height - headerSpace - footerSpace;
  const cardHeight = Math.max(isPost ? 64 : 80, Math.min(isPost ? 130 : 168, Math.floor((availableHeight - (n - 1) * rowGap) / n)));
  const pillRadius = isPost ? 16 : 26;
  const thumb = cardHeight - (isPost ? 18 : 28);

  // Timeline geometry (left gutter holds the connector line + glowing dots)
  const GUTTER = isMinimal ? 0 : (isPost ? 52 : 66);
  const dotSize = isPost ? 12 : 16;
  const rowPitch = cardHeight + rowGap;

  // Hero lines: city name is the big gradient line; fall back to the day label if no city
  const heroCity = cityName || dayLabel;
  const heroSub = customSubtitle || (cityName ? dayLabel : '');

  return (
    <div
      ref={ref}
      style={{
        width: f.width, height: f.height,
        background: t.bg,
        position: 'relative', overflow: 'hidden',
        fontFamily: fontFamily || "'Inter', sans-serif", color: t.text,
      }}
    >
      {/* Spotlight beam from the top-right corner */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: '78%', height: '58%', background: t.spotlight, filter: 'blur(6px)', pointerEvents: 'none' }} />

      <StoryHero t={t} kicker={customTitle || heroPrefix} city={heroCity} subtitle={heroSub} isPost={isPost} variant="batch" />

      <div style={{ position: 'relative', padding: isPost ? '16px 44px 0' : '28px 56px 0' }}>
        <div style={{ position: 'relative', paddingLeft: GUTTER }}>
          {/* Timeline connector */}
          {!isMinimal && n > 1 && (
            <div style={{ position: 'absolute', left: GUTTER / 2 - 1, top: cardHeight / 2, height: (n - 1) * rowPitch, width: 2, background: t.timelineLine, borderRadius: 2 }} />
          )}
          {/* Glowing nodes */}
          {!isMinimal && shown.map((ev, i) => (
            <div key={`dot-${ev._id || i}`} style={{
              position: 'absolute', left: GUTTER / 2 - dotSize / 2, top: i * rowPitch + cardHeight / 2 - dotSize / 2,
              width: dotSize, height: dotSize, borderRadius: '50%', background: t.timelineDot,
              boxShadow: `0 0 0 ${isPost ? 4 : 5}px ${t.timelineRing}, 0 0 ${isPost ? 10 : 14}px ${t.timelineGlow}`,
            }} />
          ))}
          {/* Event pills */}
          {shown.map((ev, i) => {
            const isFeatured = featuredIds?.includes(ev._id);
            const start = ev.startTime ? moment(ev.startTime).format('HH:mm') : '';
            const end = ev.endTime ? moment(ev.endTime).format('HH:mm') : '';
            const timeRange = start && end ? `${start} → ${end}` : start;
            return (
              <div key={ev._id || i} style={{
                position: 'relative', height: cardHeight, marginBottom: i < n - 1 ? rowGap : 0,
                display: 'flex', alignItems: 'center', gap: isPost ? 14 : 20,
                padding: isPost ? '0 18px' : '0 26px',
                background: isFeatured ? t.featuredBg : t.pillBg,
                border: `1px solid ${isFeatured ? t.featuredBorder : t.pillBorder}`,
                borderRadius: pillRadius,
                boxShadow: isFeatured ? `0 0 26px ${t.timelineGlow}` : 'none',
                overflow: 'hidden',
              }}>
                {isFeatured && (
                  <div style={{ position: 'absolute', top: 0, right: 0, background: t.accent, color: '#fff', fontSize: isPost ? 10 : 12, fontWeight: 700, padding: isPost ? '3px 10px 4px' : '4px 14px 5px', borderRadius: `0 ${pillRadius}px 0 12px`, letterSpacing: 1, textTransform: 'uppercase' }}>
                    Featured
                  </div>
                )}
                {!isMinimal && (
                  ev.flyer ? (
                    <img src={ev.flyer} alt="" crossOrigin="anonymous" style={{ width: thumb, height: thumb, objectFit: 'cover', borderRadius: isPost ? 10 : 16, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: thumb, height: thumb, borderRadius: isPost ? 10 : 16, background: t.cardBg, flexShrink: 0 }} />
                  )
                )}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontSize: isPost ? (isFeatured ? 22 : 20) : (isFeatured ? 31 : 27), fontWeight: 700, lineHeight: 1.15, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                  {ev.venue && (
                    <div style={{ fontSize: isPost ? 16 : 21, color: t.text, opacity: 0.7, fontWeight: 500, marginTop: isPost ? 3 : 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.venue}
                    </div>
                  )}
                </div>
                {timeRange && (
                  <div style={{ fontSize: isPost ? 18 : 24, fontWeight: 800, color: t.accent, flexShrink: 0, letterSpacing: 0.3, paddingLeft: 10, textAlign: 'right', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                    {timeRange}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {pageLabel && (
        <div style={{ position: 'absolute', bottom: showFooter ? (isPost ? 185 : 310) : (isPost ? 24 : 40), left: 0, right: 0, textAlign: 'center' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: isPost ? 18 : 24, fontWeight: 700,
            color: t.text, background: t.featuredBg,
            padding: isPost ? '8px 22px' : '12px 30px',
            borderRadius: 999,
            border: `1.5px solid ${t.featuredBorder}`,
            letterSpacing: 0.5, textTransform: 'uppercase',
          }}>
            {pageLabel} <span style={{ color: t.accent, fontSize: isPost ? 20 : 26 }}>→</span>
          </span>
        </div>
      )}

      <StoryFooter t={t} showFooter={showFooter} showLogo={showLogo} compact={isPost} />
    </div>
  );
});

// ── Theme Picker ──
const ThemePicker = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-gray-500">Theme</span>
    {Object.entries(THEMES).map(([key, t]) => (
      <div
        key={key}
        onClick={() => onChange(key)}
        title={t.label}
        style={{
          width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
          background: t.swatch,
          border: value === key ? '2.5px solid #a855f7' : '2px solid #d1d5db',
          boxShadow: value === key ? '0 0 0 2px rgba(168,85,247,0.3)' : 'none',
          transition: 'all 0.15s',
        }}
      />
    ))}
  </div>
);

// ── Main Component ──
const MAX_PER_PAGE = { story: 10, post: 6 };

const EventStoryGenerator = () => {
  const singleRef = useRef(null);
  const batchPageRefs = useRef({});

  const [mode, setMode] = useState('single');
  const [theme, setTheme] = useState('dark');
  const [format, setFormat] = useState('story');
  const [currentPage, setCurrentPage] = useState(0);

  // Customization
  const [selectedFont, setSelectedFont] = useState('inter');
  const [accentColor, setAccentColor] = useState(null);
  const [titleOverride, setTitleOverride] = useState('');
  const [customSubtitle, setCustomSubtitle] = useState('');
  const [showLogo, setShowLogo] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [exportQuality, setExportQuality] = useState(1);
  const [batchLayout, setBatchLayout] = useState('cards');

  // Video
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoType, setVideoType] = useState(null); // 'slides' | 'list'
  const videoFrameRefs = useRef({});
  const listVideoFrameRefs = useRef({});

  // Filters
  const [countries, setCountries] = useState([]);
  const [selectedCityIds, setSelectedCityIds] = useState([]);
  const selectedCity = selectedCityIds[0] || null; // The "primary" city drives the preview & accent
  const [dateRange, setDateRange] = useState([moment(), moment().add(14, 'days')]);

  // Flat list of every enabled city across all countries (drives the chip picker)
  const cities = useMemo(() => (
    (countries || []).flatMap(co => (co.cities || [])
      .filter(ct => ct.isEnabled)
      .map(ct => ({ _id: ct._id, name: ct.name, countryId: co._id, countryName: co.name }))
    ).sort((a, b) => a.name.localeCompare(b.name))
  ), [countries]);
  const cityById = useMemo(() => Object.fromEntries(cities.map(c => [c._id, c])), [cities]);
  const selectedCountryId = selectedCity ? cityById[selectedCity]?.countryId : null;

  // Multi-city release state
  const [bulkDay, setBulkDay] = useState(moment());
  const [bulkData, setBulkData] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const bulkRefs = useRef([]);
  const [countriesLoaded, setCountriesLoaded] = useState(false);

  // Events
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Selection
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]);

  // Font
  const currentFontFamily = useMemo(() => {
    const font = FONT_OPTIONS.find(f => f.key === selectedFont);
    return font?.family || "'Inter', sans-serif";
  }, [selectedFont]);

  React.useEffect(() => {
    const font = FONT_OPTIONS.find(f => f.key === selectedFont);
    if (font) loadGoogleFont(font);
  }, [selectedFont]);

  // Preload the Anton hero font once so it's embedded by html-to-image on export
  React.useEffect(() => { loadHeroFont(); }, []);

  React.useEffect(() => {
    if (countriesLoaded) return;
    (async () => {
      try {
        const res = await getActiveCountries();
        setCountries(res?.data?.data || res?.data || []);
      } catch { /* ignore */ }
      setCountriesLoaded(true);
    })();
  }, [countriesLoaded]);

  const handleLoadEvents = async () => {
    if (!selectedCity) { message.warning('Pick a city first'); return; }
    try {
      setLoading(true);
      setSelectedEventId(null);
      setSelectedEventIds([]);
      setFeaturedIds([]);
      const res = await fetchEventsForCalendar({
        city: selectedCity,
        country: selectedCountryId,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
      });
      setEvents(res?.data || []);
      setLoadedForCity(selectedCity);
      if (res?.data?.length) {
        // One-time inspect: log the first event so we can see which fields exist for the club/creator name
        // eslint-disable-next-line no-console
        console.log('[EventStoryGenerator] sample raw event →', res.data[0]);
      } else {
        message.info('No events found');
      }
    } catch { message.error('Failed to load events'); }
    finally { setLoading(false); }
  };

  // Multi-city bulk release: render one poster per selected city for the chosen day, then download all
  const handleBulkRelease = async () => {
    if (selectedCityIds.length < 2) { message.warning('Pick 2 or more cities'); return; }
    setBulkBusy(true);
    setBulkProgress({ current: 0, total: selectedCityIds.length });

    try {
      const dayStart = moment(bulkDay).startOf('day');
      const dayEnd = moment(bulkDay).endOf('day');
      const today = moment().startOf('day');
      const diff = dayStart.diff(today, 'days');
      const heroPrefix = diff === 0 ? 'TONIGHT IN' : diff === 1 ? 'TOMORROW IN' : `${dayStart.format('dddd').toUpperCase()} IN`;
      const dayLabel = dayStart.format('dddd DD MMMM');
      const perPageLimit = MAX_PER_PAGE[format] || 10;

      // Fetch all cities in parallel
      const results = await Promise.all(selectedCityIds.map(async (cityId) => {
        const city = cityById[cityId];
        if (!city) return null;
        try {
          const res = await fetchEventsForCalendar({
            city: cityId,
            country: city.countryId,
            startDate: dayStart.toISOString(),
            endDate: dayEnd.toISOString(),
          });
          const evs = (res?.data || [])
            .map(extractEvent)
            .filter(e => e.flyer)
            // Strict client-side day filter: the API only respects YYYY-MM-DD so adjacent days can leak in
            .filter(e => e.startTime && moment(e.startTime).isSame(dayStart, 'day'))
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            .slice(0, perPageLimit);
          return { cityId, cityName: city.name, accent: accentForCity(city.name), events: evs, dayLabel, heroPrefix };
        } catch (e) {
          console.error(`Bulk fetch failed for ${city.name}`, e);
          return null;
        }
      }));

      const valid = results.filter(r => r && r.events.length > 0);
      if (valid.length === 0) {
        message.info('No events with flyers found in any selected city for that day');
        return;
      }

      setBulkData(valid);

      // Wait for hidden canvases to mount + fonts to load before capturing
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));
      await document.fonts.ready;

      const fmtCfg = FORMAT_CONFIGS[format];
      const prefix = format === 'post' ? 'post' : 'story';
      const dayStr = dayStart.format('YYYY-MM-DD');

      for (let i = 0; i < valid.length; i++) {
        const item = valid[i];
        const el = bulkRefs.current[i];
        if (!el) continue;
        const dataUrl = await toPng(el, { width: fmtCfg.width, height: fmtCfg.height, pixelRatio: exportQuality, cacheBust: true });
        const link = document.createElement('a');
        const slug = item.cityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        link.download = `${prefix}-${slug}-${dayStr}.png`;
        link.href = dataUrl;
        link.click();
        setBulkProgress({ current: i + 1, total: valid.length });
        if (i < valid.length - 1) await new Promise(r => setTimeout(r, 300));
      }

      const skipped = selectedCityIds.length - valid.length;
      message.success(`Released ${valid.length} city poster${valid.length > 1 ? 's' : ''}${skipped > 0 ? ` (${skipped} skipped — no events with flyer)` : ''}!`);
    } catch (err) {
      console.error(err);
      message.error('Bulk release failed');
    } finally {
      setBulkBusy(false);
      setBulkProgress({ current: 0, total: 0 });
      setBulkData([]);
    }
  };

  const eventList = useMemo(() => events.map(extractEvent).sort((a, b) => new Date(a.startTime) - new Date(b.startTime)), [events]);

  const eventsByDay = useMemo(() => {
    const groups = {};
    eventList.forEach(ev => {
      if (!ev.startTime) return;
      const key = moment(ev.startTime).format('YYYY-MM-DD');
      if (!groups[key]) groups[key] = [];
      groups[key].push(ev);
    });
    return groups;
  }, [eventList]);

  const dayButtons = useMemo(() =>
    Object.keys(eventsByDay).sort().map(dateKey => ({
      dateKey,
      label: moment(dateKey).format('ddd DD MMM'),
      count: eventsByDay[dateKey].length,
      ids: eventsByDay[dateKey].map(e => e._id),
    }))
  , [eventsByDay]);

  const selectedEvent = useMemo(() => eventList.find(e => e._id === selectedEventId) || null, [selectedEventId, eventList]);
  // Batch lineup hides events without a flyer (the empty thumbnail looks broken on the poster).
  // The sidebar checkboxes still reflect the raw user selection — only the rendered poster filters.
  const batchEvents = useMemo(
    () => eventList.filter(e => selectedEventIds.includes(e._id) && e.flyer),
    [selectedEventIds, eventList],
  );

  // Events for video (single or batch)
  const eventsForVideo = useMemo(() => {
    if (mode === 'single' && selectedEvent) return [selectedEvent];
    if (mode === 'batch' && batchEvents.length > 0) return batchEvents;
    return [];
  }, [mode, selectedEvent, batchEvents]);

  // Progressive list frames for list-reveal video (1 item, 2 items, 3 items...)
  const listVideoSteps = useMemo(() => {
    if (mode !== 'batch' || batchEvents.length === 0) return [];
    const limit = MAX_PER_PAGE[format] || 10;
    const evs = batchEvents.slice(0, limit);
    return evs.map((_, idx) => evs.slice(0, idx + 1));
  }, [mode, batchEvents, format]);

  // Paginate batch events
  const perPage = MAX_PER_PAGE[format] || 10;
  const batchPages = useMemo(() => {
    if (batchEvents.length === 0) return [];
    const pages = [];
    for (let i = 0; i < batchEvents.length; i += perPage) {
      pages.push(batchEvents.slice(i, i + perPage));
    }
    return pages;
  }, [batchEvents, perPage]);

  const totalPages = batchPages.length;

  // Reset page when selection or format changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [selectedEventIds, format]);

  const batchDayLabel = useMemo(() => {
    if (batchEvents.length === 0) return '';
    const dates = [...new Set(batchEvents.map(e => e.startTime ? moment(e.startTime).format('YYYY-MM-DD') : null).filter(Boolean))];
    if (dates.length === 1) return moment(dates[0]).format('dddd DD MMMM');
    const d1 = moment(dates[0]);
    const d2 = moment(dates[dates.length - 1]);
    if (dates.length === 2 && ((d1.day() === 5 && d2.day() === 6) || (d1.day() === 6 && d2.day() === 0))) return 'This Weekend';
    return `${d1.format('DD MMM')} — ${d2.format('DD MMM')}`;
  }, [batchEvents]);

  const cityName = useMemo(() => (selectedCity ? cityById[selectedCity]?.name || '' : ''), [selectedCity, cityById]);

  // Auto-apply the city's default accent on city change (the ColorPicker still overrides freely)
  React.useEffect(() => {
    setAccentColor(accentForCity(cityName));
  }, [cityName]);

  // Tracks which city the currently-loaded events belong to (prevents stale preview when primary city changes)
  const [loadedForCity, setLoadedForCity] = useState(null);
  const eventsStale = loadedForCity !== null && loadedForCity !== selectedCity;

  // When the primary city changes, the loaded events no longer match — clear them so the preview never shows the wrong city's lineup
  React.useEffect(() => {
    if (eventsStale) {
      setEvents([]);
      setSelectedEventId(null);
      setSelectedEventIds([]);
      setFeaturedIds([]);
      setLoadedForCity(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  // Poster kicker line above the city name (e.g. "TONIGHT IN")
  const batchHeroPrefix = useMemo(() => {
    if (batchEvents.length === 0) return "WHAT'S ON IN";
    const dates = [...new Set(batchEvents.map(e => e.startTime ? moment(e.startTime).format('YYYY-MM-DD') : null).filter(Boolean))];
    if (dates.length === 1) {
      const d = moment(dates[0]);
      if (d.isSame(moment(), 'day')) return 'TONIGHT IN';
      if (d.isSame(moment().add(1, 'day'), 'day')) return 'TOMORROW IN';
      return `${d.format('dddd').toUpperCase()} IN`;
    }
    const d1 = moment(dates[0]);
    const d2 = moment(dates[dates.length - 1]);
    if (dates.length === 2 && ((d1.day() === 5 && d2.day() === 6) || (d1.day() === 6 && d2.day() === 0))) return 'THIS WEEKEND IN';
    return "WHAT'S ON IN";
  }, [batchEvents]);

  const singleHeroPrefix = useMemo(() => {
    if (!selectedEvent?.startTime) return 'LIVE IN';
    const d = moment(selectedEvent.startTime);
    if (d.isSame(moment(), 'day')) return 'TONIGHT IN';
    if (d.isSame(moment().add(1, 'day'), 'day')) return 'TOMORROW IN';
    return `${d.format('dddd').toUpperCase()} IN`;
  }, [selectedEvent]);

  const downloadRef = useCallback(async (ref, filename) => {
    if (!ref?.current) return;
    await document.fonts.ready;
    const fmtCfg = FORMAT_CONFIGS[format];
    const dataUrl = await toPng(ref.current, { width: fmtCfg.width, height: fmtCfg.height, pixelRatio: exportQuality, cacheBust: true });
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    link.click();
  }, [format, exportQuality]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const prefix = format === 'post' ? 'post' : 'story';
      if (mode === 'single') {
        if (!singleRef.current || !selectedEvent) return;
        const slug = selectedEvent.title.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
        await downloadRef(singleRef, `${prefix}-${slug}.png`);
      } else {
        if (totalPages === 0) return;
        const slug = batchDayLabel.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
        if (totalPages === 1) {
          await downloadRef({ current: batchPageRefs.current[0] }, `${prefix}-${slug}.png`);
        } else {
          for (let i = 0; i < totalPages; i++) {
            const ref = { current: batchPageRefs.current[i] };
            if (!ref.current) continue;
            await downloadRef(ref, `${prefix}-${slug}-p${i + 1}.png`);
            if (i < totalPages - 1) await new Promise(r => setTimeout(r, 300));
          }
        }
      }
      message.success(`${FORMAT_CONFIGS[format].label} downloaded at ${exportQuality}x!`);
    } catch (err) {
      console.error(err);
      message.error('Failed to generate image');
    } finally { setDownloading(false); }
  };

  const toggleEvent = (id) => setSelectedEventIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleFeatured = (id) => setFeaturedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Video Generation ──
  const handleGenerateVideo = async () => {
    const events = eventsForVideo;
    if (events.length === 0) { message.warning('Select events first'); return; }
    if (typeof VideoEncoder === 'undefined') {
      message.error('Video encoding not supported. Use Chrome 94+ or Edge 94+.');
      return;
    }

    setVideoGenerating(true);
    setVideoType('slides');
    setVideoProgress(0);

    try {
      const fmtCfg = FORMAT_CONFIGS[format];
      const { width, height } = fmtCfg;
      const fps = 30;
      const holdSeconds = 3;
      const fadeSeconds = 0.5;
      const holdFrames = Math.round(fps * holdSeconds);
      const fadeFrames = Math.round(fps * fadeSeconds);

      // 1. Pre-render each event as PNG
      await document.fonts.ready;
      const images = [];
      for (let i = 0; i < events.length; i++) {
        const el = videoFrameRefs.current[i];
        if (!el) continue;
        const dataUrl = await toPng(el, { width, height, pixelRatio: 1, cacheBust: true });
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; });
        images.push(img);
        setVideoProgress(Math.round(((i + 1) / events.length) * 25));
      }
      if (images.length === 0) throw new Error('No frames captured');

      // 2. Set up off-screen canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // 3. Set up mp4-muxer
      const target = new ArrayBufferTarget();
      const muxer = new Muxer({
        target,
        video: { codec: 'avc', width, height, frameRate: fps },
        fastStart: 'in-memory',
      });

      // 4. Configure VideoEncoder (H.264 High Profile)
      let codecString = 'avc1.640028'; // Level 4.0
      let config = { codec: codecString, width, height, bitrate: 4_000_000, framerate: fps };
      let { supported } = await VideoEncoder.isConfigSupported(config);
      if (!supported) {
        codecString = 'avc1.640033'; // Level 5.1
        config = { ...config, codec: codecString };
        const res2 = await VideoEncoder.isConfigSupported(config);
        if (!res2.supported) throw new Error('H.264 encoding not supported at this resolution');
      }

      const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: (e) => console.error('VideoEncoder error:', e),
      });
      encoder.configure(config);

      // 5. Render frames with Ken Burns zoom + crossfade
      let frameCount = 0;
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const nextImg = images[i + 1] || null;

        // Hold phase — subtle zoom from 1.0→1.03
        for (let f = 0; f < holdFrames; f++) {
          const zoom = 1 + 0.03 * (f / holdFrames);
          const ox = (width * (zoom - 1)) / 2;
          const oy = (height * (zoom - 1)) / 2;
          ctx.globalAlpha = 1;
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, -ox, -oy, width * zoom, height * zoom);

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: frameCount % (fps * 2) === 0 });
          vf.close();
          frameCount++;
        }

        // Crossfade to next event, or fade to black on last
        for (let f = 0; f < fadeFrames; f++) {
          const progress = f / fadeFrames;
          ctx.clearRect(0, 0, width, height);
          ctx.globalAlpha = 1;
          ctx.drawImage(img, -(width * 0.03) / 2, -(height * 0.03) / 2, width * 1.03, height * 1.03);
          if (nextImg) {
            ctx.globalAlpha = progress;
            ctx.drawImage(nextImg, 0, 0, width, height);
          } else {
            ctx.globalAlpha = progress;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, width, height);
          }
          ctx.globalAlpha = 1;

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: false });
          vf.close();
          frameCount++;
        }

        setVideoProgress(25 + Math.round(((i + 1) / images.length) * 65));
      }

      // 6. Flush encoder & finalize MP4
      setVideoProgress(92);
      await encoder.flush();
      encoder.close();
      muxer.finalize();

      // 7. Download MP4
      const blob = new Blob([target.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = `mysway-${format}-${events.length}events.mp4`;
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);

      setVideoProgress(100);
      message.success(`MP4 video exported — ${events.length} events!`);
    } catch (err) {
      console.error('Video generation failed:', err);
      message.error(`Video generation failed: ${err.message}`);
    } finally {
      setVideoGenerating(false);
      setVideoType(null);
    }
  };

  // ── List Reveal Video — items appear one by one on the batch canvas ──
  const handleGenerateListVideo = async () => {
    if (listVideoSteps.length === 0) { message.warning('Select events in batch mode first'); return; }
    if (typeof VideoEncoder === 'undefined') {
      message.error('Video encoding not supported. Use Chrome 94+ or Edge 94+.');
      return;
    }

    setVideoGenerating(true);
    setVideoType('list');
    setVideoProgress(0);

    try {
      const fmtCfg = FORMAT_CONFIGS[format];
      const { width, height } = fmtCfg;
      const fps = 30;
      const holdSeconds = 2;
      const fadeSeconds = 0.3;
      const lastHoldSeconds = 3;
      const holdFrames = Math.round(fps * holdSeconds);
      const fadeFrames = Math.round(fps * fadeSeconds);
      const lastHoldFrames = Math.round(fps * lastHoldSeconds);
      const steps = listVideoSteps.length;

      // 1. Pre-render each progressive step as PNG
      await document.fonts.ready;
      const images = [];
      for (let i = 0; i < steps; i++) {
        const el = listVideoFrameRefs.current[i];
        if (!el) continue;
        const dataUrl = await toPng(el, { width, height, pixelRatio: 1, cacheBust: true });
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; });
        images.push(img);
        setVideoProgress(Math.round(((i + 1) / steps) * 25));
      }
      if (images.length === 0) throw new Error('No frames captured');

      // 2. Canvas + muxer + encoder setup
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      const target = new ArrayBufferTarget();
      const muxer = new Muxer({
        target,
        video: { codec: 'avc', width, height, frameRate: fps },
        fastStart: 'in-memory',
      });

      let codecString = 'avc1.640028';
      let config = { codec: codecString, width, height, bitrate: 4_000_000, framerate: fps };
      let { supported } = await VideoEncoder.isConfigSupported(config);
      if (!supported) {
        codecString = 'avc1.640033';
        config = { ...config, codec: codecString };
        const res2 = await VideoEncoder.isConfigSupported(config);
        if (!res2.supported) throw new Error('H.264 encoding not supported at this resolution');
      }

      const encoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: (e) => console.error('VideoEncoder error:', e),
      });
      encoder.configure(config);

      // 3. Render frames — each step holds then crossfades to reveal next item
      let frameCount = 0;
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        const nextImg = images[i + 1] || null;
        const isLast = i === images.length - 1;
        const frames = isLast ? lastHoldFrames : holdFrames;

        // Hold phase
        for (let f = 0; f < frames; f++) {
          ctx.globalAlpha = 1;
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: frameCount % (fps * 2) === 0 });
          vf.close();
          frameCount++;
        }

        // Crossfade to next step (one more item) or fade to black
        const fadeTo = nextImg || null;
        for (let f = 0; f < fadeFrames; f++) {
          const progress = f / fadeFrames;
          ctx.clearRect(0, 0, width, height);
          ctx.globalAlpha = 1;
          ctx.drawImage(img, 0, 0, width, height);
          if (fadeTo) {
            ctx.globalAlpha = progress;
            ctx.drawImage(fadeTo, 0, 0, width, height);
          } else {
            ctx.globalAlpha = progress;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, width, height);
          }
          ctx.globalAlpha = 1;

          const vf = new VideoFrame(canvas, { timestamp: Math.round((frameCount * 1_000_000) / fps) });
          encoder.encode(vf, { keyFrame: false });
          vf.close();
          frameCount++;
        }

        setVideoProgress(25 + Math.round(((i + 1) / images.length) * 65));
      }

      // 4. Finalize
      setVideoProgress(92);
      await encoder.flush();
      encoder.close();
      muxer.finalize();

      const blob = new Blob([target.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.download = `mysway-${format}-list-reveal.mp4`;
      a.href = url;
      a.click();
      URL.revokeObjectURL(url);

      setVideoProgress(100);
      message.success(`List reveal video exported — ${images.length} steps!`);
    } catch (err) {
      console.error('List video generation failed:', err);
      message.error(`List video failed: ${err.message}`);
    } finally {
      setVideoGenerating(false);
      setVideoType(null);
    }
  };

  const canvasProps = {
    theme, format,
    fontFamily: currentFontFamily,
    accentColor,
    cityName,
    showLogo,
    showFooter,
    customTitle: titleOverride || undefined,
    customSubtitle: customSubtitle || undefined,
  };

  const fc = FORMAT_CONFIGS[format];

  return (
    <Card
      title={<span><CameraOutlined className="mr-2" />Instagram Content Generator</span>}
      className="shadow-lg mt-6"
    >
      {/* ── Toolbar Row 1: Mode + Format + Theme ── */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Segmented
          value={mode}
          onChange={(v) => { setMode(v); setSelectedEventId(null); setSelectedEventIds([]); setFeaturedIds([]); }}
          options={[
            { label: <span><PictureOutlined /> Single</span>, value: 'single' },
            { label: <span><UnorderedListOutlined /> Batch</span>, value: 'batch' },
          ]}
          size="large"
        />
        <Segmented
          value={format}
          onChange={setFormat}
          options={[
            { label: <span><PictureOutlined /> 9:16 Story</span>, value: 'story' },
            { label: <span><AppstoreOutlined /> 1:1 Post</span>, value: 'post' },
          ]}
          size="large"
        />
        <ThemePicker value={theme} onChange={setTheme} />
      </div>

      {/* ── Toolbar Row 2: Date + Load ── */}
      <div className="flex flex-wrap items-end gap-3 mb-2">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Date Range</div>
          <RangePicker value={dateRange} onChange={(d) => d && setDateRange(d)} />
        </div>
        <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={handleLoadEvents} disabled={!selectedCity}>
          Load Events
        </Button>
      </div>

      {/* ── Cities (chip multi-select; first chip = primary preview city) ── */}
      {cities.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded" style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}>
          <span className="text-xs font-medium text-gray-500 mr-1">Cities ({cities.length} enabled):</span>
          {cities.map(c => {
            const checked = selectedCityIds.includes(c._id);
            const isPrimary = selectedCityIds[0] === c._id;
            const cityAccent = accentForCity(c.name);
            return (
              <Tag.CheckableTag
                key={c._id}
                checked={checked}
                onChange={() => setSelectedCityIds(prev => (
                  prev.includes(c._id) ? prev.filter(id => id !== c._id) : [...prev, c._id]
                ))}
                style={{
                  padding: '2px 12px', fontSize: 13, fontWeight: 600, borderRadius: 999,
                  border: `1.5px solid ${checked ? (cityAccent || '#a855f7') : '#d9d9d9'}`,
                  background: checked ? (cityAccent || '#a855f7') : '#fff',
                  color: checked ? '#fff' : '#555',
                }}
              >
                {isPrimary && '★ '}{c.name}
              </Tag.CheckableTag>
            );
          })}
          {selectedCityIds.length > 0 && (
            <Button size="small" type="link" onClick={() => setSelectedCityIds([])}>Clear</Button>
          )}
          {selectedCityIds.length >= 2 && (
            <span className="text-xs text-gray-400 ml-2">★ primary drives the preview · all selected get released in bulk</span>
          )}
        </div>
      )}

      {/* ── Multi-city Release ── */}
      {selectedCityIds.length >= 2 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 p-2 rounded" style={{ background: '#faf5ff', border: '1px solid #e9d5ff' }}>
          <span className="text-xs font-semibold text-purple-600">Multi-city Release</span>
          <DatePicker
            value={bulkDay}
            onChange={(d) => d && setBulkDay(d)}
            allowClear={false}
            size="small"
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={bulkBusy}
            onClick={handleBulkRelease}
            size="small"
            style={{ background: '#a855f7', borderColor: '#a855f7' }}
          >
            Release {selectedCityIds.length} cities for {bulkDay.format('ddd DD MMM')}
          </Button>
          {bulkBusy && bulkProgress.total > 0 && (
            <span className="text-xs text-purple-600 font-semibold">
              Generating {bulkProgress.current} / {bulkProgress.total}…
            </span>
          )}
          <span className="text-xs text-gray-500 w-full">
            ⓘ Bulk fetches each city independently — top {MAX_PER_PAGE[format] || 10} events with a flyer for {bulkDay.format('ddd DD MMM')}. The preview on the right reflects only the primary city's manually-curated selection.
          </span>
        </div>
      )}

      {/* ── Customize Panel ── */}
      <Collapse
        ghost
        style={{ marginBottom: 12 }}
        items={[{
          key: 'customize',
          label: <span className="text-sm"><SettingOutlined className="mr-1" />Customize</span>,
          children: (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-2">
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1"><FontSizeOutlined /> Font</div>
                <Select value={selectedFont} onChange={setSelectedFont} style={{ width: '100%' }} optionLabelProp="label">
                  {FONT_OPTIONS.map(f => (
                    <Option key={f.key} value={f.key} label={f.label}>
                      <div className="flex justify-between items-center">
                        <span style={{ fontFamily: f.family }}>{f.label}</span>
                        <span className="text-xs text-gray-400">{f.category}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1"><BgColorsOutlined /> Accent Color {cityName && <span className="text-gray-400 font-normal">· {cityName} default</span>}</div>
                <div className="flex items-center gap-2">
                  <ColorPicker
                    value={accentColor || THEMES[theme].accent}
                    onChange={(_, hex) => setAccentColor(hex)}
                    size="small"
                  />
                  {accentColor !== accentForCity(cityName) && (
                    <Button size="small" type="link" onClick={() => setAccentColor(accentForCity(cityName))}>Reset</Button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Headline kicker</div>
                <Input value={titleOverride} onChange={(e) => setTitleOverride(e.target.value)} placeholder="e.g. TONIGHT IN" size="small" allowClear />
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Subtitle / Date</div>
                <Input value={customSubtitle} onChange={(e) => setCustomSubtitle(e.target.value)} placeholder="e.g. Thursday 28 May" size="small" allowClear />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <Switch checked={showLogo} onChange={setShowLogo} size="small" />
                  <span className="text-xs text-gray-600">App icon</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Switch checked={showFooter} onChange={setShowFooter} size="small" />
                  <span className="text-xs text-gray-600">Footer</span>
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Export Quality</div>
                <Segmented value={exportQuality} onChange={setExportQuality} options={EXPORT_QUALITY.map(q => ({ label: q.label, value: q.key }))} size="small" />
              </div>
              {mode === 'batch' && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">Batch Layout</div>
                  <Segmented value={batchLayout} onChange={setBatchLayout} options={Object.entries(BATCH_LAYOUTS).map(([k, v]) => ({ label: v.label, value: k }))} size="small" />
                </div>
              )}
            </div>
          ),
        }]}
      />

      {loading && <div className="text-center py-8"><Spin size="large" /></div>}

      {!loading && events.length > 0 && (
        <div className="flex gap-6" style={{ minHeight: 600 }}>
          {/* Left: Event list */}
          <div style={{ width: 380, flexShrink: 0, maxHeight: 720, overflowY: 'auto' }} className="pr-2">
            {mode === 'batch' && dayButtons.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {dayButtons.map(d => (
                  <Tag
                    key={d.dateKey}
                    color={selectedEventIds.length > 0 && d.ids.every(id => selectedEventIds.includes(id)) ? 'blue' : 'default'}
                    className="cursor-pointer"
                    style={{ fontSize: 13, padding: '2px 10px' }}
                    onClick={() => {
                      const allSelected = d.ids.every(id => selectedEventIds.includes(id));
                      if (allSelected) {
                        setSelectedEventIds(prev => prev.filter(id => !d.ids.includes(id)));
                      } else {
                        setSelectedEventIds(prev => [...new Set([...prev, ...d.ids])]);
                      }
                    }}
                  >
                    {d.label} ({d.count})
                  </Tag>
                ))}
                {selectedEventIds.length > 0 && (
                  <Tag color="red" className="cursor-pointer" style={{ fontSize: 13, padding: '2px 10px' }} onClick={() => { setSelectedEventIds([]); setFeaturedIds([]); }}>
                    Clear
                  </Tag>
                )}
              </div>
            )}

            <div className="text-sm font-semibold text-gray-600 mb-2">
              {eventList.length} Events
              {mode === 'batch' && selectedEventIds.length > 0 && (
                <span className="text-blue-500 ml-2">
                  ({selectedEventIds.length} selected
                  {batchEvents.length !== selectedEventIds.length && <span className="text-gray-400"> · {batchEvents.length} with flyer</span>})
                </span>
              )}
              {mode === 'batch' && featuredIds.length > 0 && <span className="text-purple-500 ml-2">({featuredIds.length} featured)</span>}
            </div>

            {mode === 'single' ? (
              <Radio.Group value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} className="w-full">
                <Space direction="vertical" className="w-full">
                  {eventList.map(ev => (
                    <Radio key={ev._id} value={ev._id} className="w-full">
                      <EventListItem ev={ev} />
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            ) : (
              <Space direction="vertical" className="w-full">
                {eventList.map(ev => (
                  <div key={ev._id} className="flex items-center gap-1">
                    <Checkbox checked={selectedEventIds.includes(ev._id)} onChange={() => toggleEvent(ev._id)}>
                      <EventListItem ev={ev} />
                    </Checkbox>
                    {selectedEventIds.includes(ev._id) && (
                      <Tooltip title={featuredIds.includes(ev._id) ? 'Remove featured' : 'Mark as featured'}>
                        <Button
                          type="text" size="small"
                          onClick={() => toggleFeatured(ev._id)}
                          style={{ color: featuredIds.includes(ev._id) ? '#a855f7' : '#ccc', padding: '0 4px' }}
                          icon={featuredIds.includes(ev._id) ? <StarFilled /> : <StarOutlined />}
                        />
                      </Tooltip>
                    )}
                  </div>
                ))}
              </Space>
            )}
          </div>

          {/* Right: Story preview */}
          <div className="flex-1 flex flex-col items-center min-w-0 overflow-hidden">
            {mode === 'single' && selectedEvent && (
              <>
                <div style={{ width: fc.width * fc.previewScale, height: fc.height * fc.previewScale, maxWidth: '100%' }}>
                  <div style={{ width: fc.width, height: fc.height, transform: `scale(${fc.previewScale})`, transformOrigin: 'top left' }}>
                    <SingleStoryCanvas ref={singleRef} event={selectedEvent} heroPrefix={singleHeroPrefix} {...canvasProps} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3" style={{ marginTop: 16 }}>
                  <Segmented
                    value={format}
                    onChange={setFormat}
                    options={[
                      { label: <span><PictureOutlined /> 9:16</span>, value: 'story' },
                      { label: <span><AppstoreOutlined /> 1:1</span>, value: 'post' },
                    ]}
                  />
                  <Button type="primary" size="large" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
                    Download {fc.label}
                  </Button>
                  <Button size="large" icon={<VideoCameraOutlined />} loading={videoGenerating && videoType === 'slides'} disabled={videoGenerating} onClick={handleGenerateVideo}>
                    Generate Video
                  </Button>
                </div>
                {videoGenerating && (
                  <div style={{ width: 300, marginTop: 12 }}>
                    <Progress percent={videoProgress} size="small" status="active" format={(p) => `${p}%`} />
                    <div className="text-xs text-gray-400 text-center mt-1">
                      {videoProgress < 25 ? 'Rendering frames...' : videoProgress < 90 ? 'Encoding video...' : 'Finalizing MP4...'}
                    </div>
                  </div>
                )}
              </>
            )}

            {mode === 'batch' && batchPages.length > 0 && (
              <>
                {/* Render all pages for download capture — only current page visible */}
                <div style={{ position: 'relative', width: fc.width * fc.previewScale, height: fc.height * fc.previewScale, maxWidth: '100%' }}>
                  {batchPages.map((pageEvents, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: 'absolute', top: 0, left: 0,
                        width: fc.width, height: fc.height,
                        transform: `scale(${fc.previewScale})`, transformOrigin: 'top left',
                        display: idx === currentPage ? 'block' : 'none',
                      }}
                    >
                      <BatchStoryCanvas
                        ref={el => { batchPageRefs.current[idx] = el; }}
                        events={pageEvents}
                        dayLabel={batchDayLabel}
                        cityName={cityName}
                        featuredIds={featuredIds}
                        batchLayout={batchLayout}
                        heroPrefix={batchHeroPrefix}
                        pageLabel={totalPages > 1 ? 'View the full list on the app' : undefined}
                        {...canvasProps}
                      />
                    </div>
                  ))}
                  {/* Hidden pages for download capture */}
                  {batchPages.map((pageEvents, idx) => idx !== currentPage && (
                    <div key={`hidden-${idx}`} style={{ position: 'absolute', left: -9999, top: 0 }}>
                      <BatchStoryCanvas
                        ref={el => { batchPageRefs.current[idx] = el; }}
                        events={pageEvents}
                        dayLabel={batchDayLabel}
                        cityName={cityName}
                        featuredIds={featuredIds}
                        batchLayout={batchLayout}
                        heroPrefix={batchHeroPrefix}
                        pageLabel={totalPages > 1 ? 'View the full list on the app' : undefined}
                        {...canvasProps}
                      />
                    </div>
                  ))}
                </div>

                {/* Page navigation */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-3" style={{ marginTop: 12 }}>
                    <Button icon={<LeftOutlined />} disabled={currentPage === 0} onClick={() => setCurrentPage(p => p - 1)} />
                    <span className="text-sm font-medium text-gray-600">Page {currentPage + 1} of {totalPages}</span>
                    <Button icon={<RightOutlined />} disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} />
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3" style={{ marginTop: 12 }}>
                  <Segmented
                    value={format}
                    onChange={setFormat}
                    options={[
                      { label: <span><PictureOutlined /> 9:16</span>, value: 'story' },
                      { label: <span><AppstoreOutlined /> 1:1</span>, value: 'post' },
                    ]}
                  />
                  <Button type="primary" size="large" icon={<DownloadOutlined />} loading={downloading} onClick={handleDownload}>
                    {totalPages > 1 ? `Download All ${totalPages} Pages` : `Download ${fc.label}`}
                  </Button>
                  <Button size="large" icon={<VideoCameraOutlined />} loading={videoGenerating && videoType === 'slides'} disabled={videoGenerating} onClick={handleGenerateVideo}>
                    Slides Video
                  </Button>
                  <Button size="large" icon={<UnorderedListOutlined />} loading={videoGenerating && videoType === 'list'} disabled={videoGenerating} onClick={handleGenerateListVideo}>
                    List Reveal
                  </Button>
                </div>
                {videoGenerating && (
                  <div style={{ width: 300, marginTop: 12 }}>
                    <Progress percent={videoProgress} size="small" status="active" format={(p) => `${p}%`} />
                    <div className="text-xs text-gray-400 text-center mt-1">
                      {videoProgress < 25 ? 'Rendering frames...' : videoProgress < 90 ? 'Encoding video...' : 'Finalizing MP4...'}
                    </div>
                  </div>
                )}
              </>
            )}

            {((mode === 'single' && !selectedEvent) || (mode === 'batch' && batchEvents.length === 0)) && (
              <div className="flex items-center justify-center h-full text-gray-400">
                {mode === 'single' ? 'Select an event to preview' : 'Select events or click a day tag'}
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && events.length === 0 && countriesLoaded && (
        <Empty description="Load events from a city to get started" />
      )}

      {/* Hidden video frames for slides capture */}
      {eventsForVideo.length > 0 && (
        <div style={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }}>
          {eventsForVideo.map((ev, idx) => (
            <SingleStoryCanvas
              key={ev._id}
              ref={el => { videoFrameRefs.current[idx] = el; }}
              event={ev}
              heroPrefix={singleHeroPrefix}
              {...canvasProps}
            />
          ))}
        </div>
      )}

      {/* Hidden bulk-release frames — one BatchStoryCanvas per selected city, each with its own accent */}
      {bulkData.length > 0 && (
        <div style={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }}>
          {bulkData.map((item, idx) => (
            <BatchStoryCanvas
              key={item.cityId}
              ref={el => { bulkRefs.current[idx] = el; }}
              events={item.events}
              dayLabel={item.dayLabel}
              cityName={item.cityName}
              heroPrefix={item.heroPrefix}
              featuredIds={[]}
              batchLayout={batchLayout}
              theme={theme}
              format={format}
              fontFamily={currentFontFamily}
              accentColor={item.accent}
              showLogo={showLogo}
              showFooter={showFooter}
              customTitle={undefined}
              customSubtitle={undefined}
            />
          ))}
        </div>
      )}

      {/* Hidden progressive list frames for list-reveal capture */}
      {listVideoSteps.length > 0 && (
        <div style={{ position: 'absolute', left: -99999, top: 0, pointerEvents: 'none' }}>
          {listVideoSteps.map((stepEvents, idx) => (
            <BatchStoryCanvas
              key={idx}
              ref={el => { listVideoFrameRefs.current[idx] = el; }}
              events={stepEvents}
              dayLabel={batchDayLabel}
              cityName={cityName}
              heroPrefix={batchHeroPrefix}
              featuredIds={featuredIds}
              batchLayout={batchLayout}
              {...canvasProps}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

const EventListItem = ({ ev }) => {
  const start = ev.startTime ? moment(ev.startTime).format('ddd DD MMM · HH:mm') : '—';
  const end = ev.endTime ? moment(ev.endTime).format('HH:mm') : '';
  const timeLine = end ? `${start} → ${end}` : start;
  return (
    <div className="flex items-center gap-2 py-1">
      {ev.flyer ? (
        <img src={ev.flyer} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" />
      )}
      <div className="min-w-0">
        <div className="text-sm font-medium truncate" style={{ maxWidth: 240 }}>{ev.title}</div>
        <div className="text-xs text-gray-400 truncate" style={{ maxWidth: 240 }}>{timeLine}</div>
        <div
          className="text-xs truncate"
          style={{ maxWidth: 240, color: ev.venue ? '#a855f7' : '#ef4444', fontWeight: 600 }}
          title={ev.venue || 'No club/creator name found in publication data'}
        >
          {ev.venue ? `🏢 ${ev.venue}` : '⚠ no club/creator'}
        </div>
      </div>
    </div>
  );
};

export default EventStoryGenerator;
